import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DialogComponent } from '@app/components/dialog/dialog.component';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { SaveButton } from '@components/button/save-button/save-button/save-button';
import { InputComponent } from '@app/components/input/input.component';
import { TextareaComponent } from '@app/components/textarea/textarea.component';
import { TagSelectorComponent } from '@app/components/tag-selector/tag-selector.component';
import { UploadedGeoImageListItem, UploadedGeoImageUpdateRequest } from '../services/uploaded-geo-image.models';

interface GeoImageEditForm {
  title: string;
  description: string;
  tagIds: string[];
}

/**
 * Edits an uploaded geo-image's title, description, and tags (FR-014). The geometry, capture date, and files
 * are deliberately not editable here — the server ignores them on this endpoint as well.
 */
@Component({
  selector: 'app-geo-image-edit-modal',
  standalone: true,
  templateUrl: './geo-image-edit-modal.component.html',
  styleUrls: ['./geo-image-edit-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, TranslateModule, DialogComponent, SecondaryButton, SaveButton, InputComponent, TextareaComponent, TagSelectorComponent]
})
export class GeoImageEditModalComponent implements OnChanges {
  @Input() visible = false;
  @Input() item: UploadedGeoImageListItem | null = null;
  @Input() loading = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() cancel = new EventEmitter<void>();
  @Output() save = new EventEmitter<UploadedGeoImageUpdateRequest>();

  form: GeoImageEditForm = { title: '', description: '', tagIds: [] };

  ngOnChanges(changes: SimpleChanges): void {
    const opening = changes['visible'] && this.visible;
    const itemChanged = changes['item'] && !this.visible;
    if ((opening || itemChanged) && this.item) {
      this.form = {
        title: this.item.title ?? '',
        description: this.item.description ?? '',
        tagIds: this.item.tags?.map(tag => tag.id) ?? []
      };
    }
  }

  get canSave(): boolean {
    return !this.loading && this.form.title.trim().length > 0;
  }

  onTagIdsChange(tagIds: string[]): void {
    this.form = { ...this.form, tagIds };
  }

  handleCancel(): void {
    this.visibleChange.emit(false);
    this.cancel.emit();
  }

  handleSave(): void {
    if (!this.item || !this.canSave) {
      return;
    }

    this.save.emit({
      id: this.item.id,
      title: this.form.title.trim(),
      // An empty string clears the description on the server; omitting it would keep the old one.
      description: this.form.description?.trim() ?? '',
      tagIds: this.form.tagIds
    });
  }
}
